import { useLayoutEffect, useMemo, useRef } from 'react'
import type { CSSProperties } from 'react'
import gsap from 'gsap'
import { getInviteFont, loadInviteFont } from '../../lib/inviteStyle'
import { kickerFor, paintedDate, popupAsset, readableOnPaper } from '../../lib/paintedInvite'
import type { PaintedChoice, PaintedDef, PopupLayer } from '../../lib/paintedInvite'
import './popup-card.css'

/**
 * A pop-up invitation: a paper card that opens on a table and unfolds in 3D.
 * The back panel lifts, each die-cut layer stands up from the floor panel one
 * after another, the floaters pop out in front of the back panel, and last
 * the sign rises with the event's own words. Then the layers sway, the
 * floaters bob, and dragging across the card tilts it.
 *
 * Pure CSS 3D under one perspective, driven by a GSAP timeline that tweens a
 * plain state object; one ticker callback writes every transform, so the
 * entrance and the idle never fight over a property. Lengths are card units
 * (`--u`, 0.7cqw: the card is 100 units wide), which keeps the whole fold in
 * the same 2:3 slot the painted cards use, at any size.
 *
 * `mode="thumb"` is a pre-rendered still of the open card with the title set
 * on its sign, cheap enough for a grid. Reduced motion gets the open card
 * without the entrance or the idle.
 */

type Words = {
  title: string
  honoree?: string | null
  date?: string | null
  time?: string | null
  location?: string | null
}

type Props = {
  def: PaintedDef
  choice: PaintedChoice
  words: Words
  lang?: string
  font?: string | null
  mode?: 'full' | 'thumb'
  play?: boolean
  bare?: boolean
  className?: string
  style?: CSSProperties
}

const UNIT = 0.007
const OPEN_CAM = 56
const OPEN_PAN = 29
const OPEN_DOLLY = -42
const CLOSED_DOLLY = 34
const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v))

const dateLine = (date?: string | null, time?: string | null, lang = 'en') => {
  const d = paintedDate(date, time, lang)
  if (!d.day) return d.time
  const day = `${d.day} ${d.month}`
  return d.time ? `${day} · ${d.time}` : day
}

const PopupCard = ({ def, choice, words, lang = 'en', font, mode = 'full', play = false, bare = false, className = '', style }: Props) => {
  const art = def.popup!
  const full = mode === 'full'
  const ka = lang === 'ka'
  const rootRef = useRef<HTMLDivElement>(null)
  const worldRef = useRef<HTMLDivElement>(null)
  const backRef = useRef<HTMLDivElement>(null)
  const creaseRef = useRef<HTMLDivElement>(null)
  const layerRefs = useRef<Array<HTMLDivElement | null>>([])
  const imgRefs = useRef<Array<HTMLImageElement | null>>([])
  const shadowRefs = useRef<Array<HTMLDivElement | null>>([])
  const floatRefs = useRef<Array<HTMLDivElement | null>>([])
  const lineRefs = useRef<Array<HTMLParagraphElement | null>>([])
  const textRef = useRef<HTMLDivElement>(null)

  const title = (words.title || '').trim()
  const kicker = kickerFor(def, lang)
  const when = dateLine(words.date, words.time, lang)
  const place = (words.location || '').trim()
  const own = getInviteFont(def.font)
  const picked = font ? getInviteFont(font) : null
  const type = picked || own
  const hero = useMemo(
    () =>
      art.layers
        .filter((l) => !l.plaque && l.iw / l.ih < 2)
        .reduce<PopupLayer | null>((best, l) => (!best || l.w * ((l.w * l.ih) / l.iw) > best.w * ((best.w * best.ih) / best.iw) ? l : best), null),
    [art],
  )
  const plaqueIndex = art.layers.findIndex((l) => l.plaque)
  const titleInk = readableOnPaper(choice.accent, art.plaque, 3)
  const ink = readableOnPaper(def.ink, art.plaque, 4.5)
  const wordsKey = `${title}|${when}|${place}|${kicker}|${font || ''}`

  useLayoutEffect(() => {
    loadInviteFont(def.font)
    if (font) loadInviteFont(font)
  }, [def.font, font])

  useLayoutEffect(() => {
    const root = rootRef.current
    const box = textRef.current
    if (!full || !root || !box) return undefined
    const stack = box.firstElementChild as HTMLElement
    const kickerLine = stack.firstElementChild as HTMLElement
    const shrink = () => {
      let f = 1
      box.style.setProperty('--pu-fit', '1')
      while (f > 0.4 && (stack.offsetHeight > box.clientHeight * 0.92 || stack.scrollWidth > box.clientWidth + 1)) {
        f -= 0.04
        box.style.setProperty('--pu-fit', f.toFixed(2))
      }
      return f
    }
    // A long title on a small sign would shrink everything past reading size;
    // the kicker is the one line the sign can do without.
    const fit = () => {
      kickerLine.style.display = ''
      if (shrink() < 0.64) {
        kickerLine.style.display = 'none'
        shrink()
      }
    }
    fit()
    const ro = new ResizeObserver(fit)
    ro.observe(root)
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts
    fonts?.ready.then(fit)
    fonts?.addEventListener?.('loadingdone', fit)
    return () => {
      ro.disconnect()
      fonts?.removeEventListener?.('loadingdone', fit)
    }
  }, [full, wordsKey, lang, def.id])

  useLayoutEffect(() => {
    const root = rootRef.current
    const world = worldRef.current
    if (!full || !root || !world) return undefined
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const S = {
      cam: 0,
      cover: -180,
      pan: -art.bh / 2,
      dolly: CLOSED_DOLLY,
      idle: 0,
      reveal: 0,
      layers: art.layers.map(() => ({ a: 0 })),
      floats: art.floaters.map(() => ({ s: 0 })),
    }
    const open = () => {
      S.cam = OPEN_CAM
      S.cover = -78
      S.pan = OPEN_PAN
      S.dolly = OPEN_DOLLY
      S.reveal = 1
      S.layers.forEach((l, i) => { l.a = -90 + art.layers[i].lean })
      S.floats.forEach((f) => { f.s = 1 })
    }
    const pointer = { x: 0, y: 0, tx: 0, ty: 0 }
    let u = root.clientWidth * UNIT
    const sizeRo = new ResizeObserver(() => { u = root.clientWidth * UNIT })
    sizeRo.observe(root)

    const render = () => {
      const time = gsap.ticker.time
      pointer.x += (pointer.tx - pointer.x) * 0.06
      pointer.y += (pointer.ty - pointer.y) * 0.06
      const idle = reduce ? 0 : S.idle
      const cam = S.cam + pointer.y * 6 * idle
      const spin = pointer.x * 12 * idle + Math.sin(time * 0.3) * 1.5 * idle
      world.style.transform = `translate3d(0, ${S.pan * u}px, ${S.dolly * u}px) rotateX(${cam}deg) rotateZ(${spin}deg)`
      if (backRef.current) backRef.current.style.transform = `translateZ(${0.6 * u}px) rotateX(${S.cover}deg)`
      if (creaseRef.current) creaseRef.current.style.opacity = String(clamp((S.cover + 180) / 100, 0, 1))
      art.layers.forEach((l, i) => {
        const el = layerRefs.current[i]
        if (!el) return
        const a = S.layers[i].a
        const sway = Math.sin(time * (0.55 + i * 0.13) + i * 1.7) * (l.plaque ? 0.5 : 1.3) * idle
        el.style.transform = `rotateX(${a + sway}deg)`
        el.style.opacity = clamp(-a / 14, 0, 1).toFixed(3)
        el.style.visibility = a > -0.5 ? 'hidden' : 'visible'
        const k = clamp(-a / 90, 0, 1)
        const img = imgRefs.current[i]
        if (img) img.style.filter = k >= 1 ? 'none' : `brightness(${(0.7 + 0.3 * k).toFixed(3)})`
        const sh = shadowRefs.current[i]
        if (sh) sh.style.opacity = (k * 0.6).toFixed(3)
      })
      art.floaters.forEach((f, i) => {
        const el = floatRefs.current[i]
        if (!el) return
        const s = S.floats[i].s
        const t = time + i * 2.3
        let tr = `translateZ(${f.z * u * s}px)`
        if (f.anim === 'bob') tr += ` translateY(${Math.sin(t * 1.1) * 1.6 * u * idle}px) rotate(${Math.sin(t * 0.8) * 4 * idle}deg)`
        if (f.anim === 'glide') tr += ` translateY(${Math.sin(t * 0.9) * 2.2 * u * idle}px) rotate(${Math.sin(t * 0.55) * 2.5 * idle}deg)`
        if (f.anim === 'twinkle') tr += ` rotate(${t * 30 * idle}deg) scale(${1 + Math.sin(t * 2.4) * 0.14 * idle})`
        if (f.anim === 'swing') tr += ` rotate(${Math.sin(t * 0.9) * 1.4 * idle}deg)`
        if (f.anim === 'flutter') tr += ` translate(${Math.cos(t * 0.6) * 16 * u * idle}px, ${Math.sin(t * 1.2) * 7 * u * idle}px) scaleX(${idle ? 0.55 + 0.45 * Math.abs(Math.sin(t * 13)) : 1})`
        el.style.transform = `${tr} scale(${s})`
        el.style.visibility = s <= 0.001 ? 'hidden' : 'visible'
      })
      lineRefs.current.forEach((line, k) => {
        if (!line) return
        const p = clamp(S.reveal * 5 - k * 1.15, 0, 1)
        line.style.opacity = String(p)
        line.style.transform = `translateY(${((1 - p) * 0.5).toFixed(3)}em) scale(${k === 1 ? 0.92 + 0.08 * p : 1})`
      })
    }

    let tl: gsap.core.Timeline | null = null
    if (reduce) {
      open()
      render()
    } else if (play) {
      const n = art.layers.length
      const plaqueAt = 1.45 + (n - 1) * 0.28 + 0.2
      tl = gsap.timeline()
      tl.to(S, { cover: -78, duration: 2.4, ease: 'power2.inOut' }, 0.15)
      tl.to(S, { cam: OPEN_CAM, pan: OPEN_PAN, dolly: OPEN_DOLLY, duration: 2.9, ease: 'power2.inOut' }, 0.25)
      art.layers.forEach((l, i) => {
        const at = l.plaque ? plaqueAt : 1.6 + i * 0.28
        tl!.to(S.layers[i], { a: -90 + l.lean, duration: l.plaque ? 1.3 : 1.5, ease: l.plaque ? 'back.out(1.3)' : 'back.out(1.7)' }, at)
      })
      art.floaters.forEach((_, i) => tl!.to(S.floats[i], { s: 1, duration: 0.9, ease: 'back.out(2.2)' }, 2.05 + i * 0.25))
      tl.to(S, { reveal: 1, duration: 1.4, ease: 'none' }, plaqueAt + 0.7)
      tl.to(S, { idle: 1, duration: 1.6, ease: 'power1.inOut' }, plaqueAt + 1.3)
    }
    if (!reduce) gsap.ticker.add(render)
    render()

    const move = (e: PointerEvent) => {
      const r = root.getBoundingClientRect()
      pointer.tx = clamp(((e.clientX - r.left) / r.width) * 2 - 1, -1, 1)
      pointer.ty = clamp(((e.clientY - r.top) / r.height) * 2 - 1, -1, 1)
    }
    const leave = () => { pointer.tx = 0; pointer.ty = 0 }
    root.addEventListener('pointermove', move)
    root.addEventListener('pointerleave', leave)
    return () => {
      tl?.kill()
      gsap.ticker.remove(render)
      sizeRo.disconnect()
      root.removeEventListener('pointermove', move)
      root.removeEventListener('pointerleave', leave)
    }
  }, [full, play, art, def.id])

  const vars = {
    '--pu-t1': art.table[0],
    '--pu-t2': art.table[1],
    '--pu-floor': art.floor,
    '--pu-dot': art.dot,
    '--pu-edge': art.edge,
    '--pu-cover': art.cover,
    '--pu-cover-ink': art.coverInk,
    '--pu-ink': ink,
    '--pu-title-ink': titleInk,
    '--pu-title-font': type?.display,
    '--pu-body-font': type?.body,
    '--pu-title-weight': picked ? 700 : art.titleWeight,
    '--pu-title-size': art.titleSize,
    '--pu-shadow': art.lightText ? '0 1px 1px rgba(40,15,0,.8), 0 0 8px rgba(60,25,5,.6)' : 'none',
    '--pu-bh': art.bh,
    ...style,
  } as CSSProperties

  if (!full) {
    const tt = art.thumbText
    return (
      <div className={`pu pu-thumb${bare ? ' pu-bare' : ''} ${className}`} style={vars} lang={ka ? 'ka' : 'en'}>
        <img className="pu-thumb-img" src={popupAsset(def, 'thumb')} alt="" loading="lazy" draggable={false} />
        {!bare && (
          <div className="pu-thumb-text" style={{ left: `${tt.left}%`, top: `${tt.top}%`, width: `${tt.width}%`, height: `${tt.height}%`, '--pu-tw': tt.width } as CSSProperties}>
            <p className="pu-tt">{title}</p>
            {when && <p className="pu-td">{when}</p>}
          </div>
        )}
      </div>
    )
  }

  return (
    <div ref={rootRef} className={`pu pu-full${bare ? ' pu-bare' : ''} ${className}`} style={vars} lang={ka ? 'ka' : 'en'}>
      <div className="pu-scene">
        <div className="pu-world" ref={worldRef}>
          <div className="pu-floor">
            <div className="pu-crease" ref={creaseRef} />
            {art.layers.map((l, i) => (
              <div
                key={`s-${l.key}`}
                className="pu-shadow"
                ref={(n) => { shadowRefs.current[i] = n }}
                style={{ left: `calc(var(--u) * ${l.x - l.w * 0.45})`, top: `calc(var(--u) * ${l.y - 3.5})`, width: `calc(var(--u) * ${l.w * 0.9})` }}
              />
            ))}
          </div>
          <div className="pu-back" ref={backRef}>
            <div className="pu-inner" style={{ backgroundImage: `url("${popupAsset(def, 'back')}")` }} />
            <div className="pu-cover">
              <p className="pu-c-kicker">{ka ? 'გეპატიჟებით' : 'You’re invited'}</p>
              {hero && <img src={popupAsset(def, hero.key)} alt="" draggable={false} />}
              <p className="pu-c-title">{title}</p>
            </div>
            {art.floaters.map((f, i) => (
              <div
                key={`f-${i}`}
                className={`pu-float${f.anim === 'swing' ? ' pu-hang' : ''}`}
                ref={(n) => { floatRefs.current[i] = n }}
                style={{ left: `calc(var(--u) * ${f.x - f.w / 2})`, top: `calc(var(--u) * ${f.top})`, width: `calc(var(--u) * ${f.w})`, height: `calc(var(--u) * ${(f.w * f.ih) / f.iw})` }}
              >
                <img src={popupAsset(def, f.key)} alt="" draggable={false} />
              </div>
            ))}
          </div>
          {art.layers.map((l, i) => {
            const h = (l.w * l.ih) / l.iw
            return (
              <div
                key={`l-${l.key}`}
                className="pu-pop"
                ref={(n) => { layerRefs.current[i] = n }}
                style={{ left: `calc(var(--u) * ${l.x - 50 - l.w / 2})`, top: `calc(var(--u) * ${l.y - h})`, width: `calc(var(--u) * ${l.w})`, height: `calc(var(--u) * ${h})`, '--pw': `calc(var(--u) * ${l.w})` } as CSSProperties}
              >
                <img ref={(n) => { imgRefs.current[i] = n }} src={popupAsset(def, l.key)} alt="" draggable={false} />
                {i === plaqueIndex && l.plaque && (
                  <div
                    className="pu-text"
                    ref={textRef}
                    style={{ left: `${l.plaque[0] * 100}%`, top: `${l.plaque[1] * 100}%`, right: `${l.plaque[2] * 100}%`, bottom: `${l.plaque[3] * 100}%` }}
                  >
                    <div className="pu-stack">
                      <p className="pu-kicker" ref={(n) => { lineRefs.current[0] = n }}>{kicker}</p>
                      <p className="pu-title" ref={(n) => { lineRefs.current[1] = n }}>{title}</p>
                      {when && <p className="pu-when" ref={(n) => { lineRefs.current[2] = n }}>{when}</p>}
                      {place && <p className="pu-where" ref={(n) => { lineRefs.current[3] = n }}>{place}</p>}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default PopupCard
